// Cart Drawer Custom Element - Based on imported theme pattern
// This creates a reliable cart drawer that works consistently

class CartDrawerElement extends HTMLElement {
  constructor() {
    super();
    this.isRefreshing = false;
    this.activeElement = null;
    this.onOverlayClick = this.handleOverlayClick.bind(this);
    this.onEscapeKey = this.handleEscapeKey.bind(this);
    this.onCartUpdated = this.handleCartUpdated.bind(this);
    this._initialized = false;
  }
  
  connectedCallback() {
    // Defer initialization to ensure DOM is ready
    if (!this._initialized) {
      // Use requestAnimationFrame to ensure DOM children are available
      requestAnimationFrame(() => {
        this.initializeElements();
        this.init();
        this._initialized = true;
      });
    }
  }
  
  initializeElements() {
    this.drawer = this.querySelector('.cart-drawer');
    this.overlay = this.querySelector('[data-cart-drawer-overlay]');
    this.panel = this.querySelector('[data-cart-drawer-panel]');
    this.closeButtons = this.querySelectorAll('[data-cart-drawer-close]');
    this.itemsContainer = this.querySelector('[data-cart-drawer-items]');
    this.footer = this.querySelector('[data-cart-footer]');
    this.emptyState = this.querySelector('[data-cart-empty]');
  }

  init() {
    // Close buttons
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', this.onOverlayClick);
    }

    // ESC key - only add once
    if (!this.escapeKeyListenerAdded) {
      document.addEventListener('keydown', this.onEscapeKey);
      this.escapeKeyListenerAdded = true;
    }

    // Listen for cart updates - only add once to prevent multiple listeners
    if (!this.cartUpdateListenerAdded) {
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.cartUpdateListenerAdded = true;
    }
  }

  handleCartUpdated() {
    // Only refresh if drawer is currently open
    if (!this.classList.contains('active')) {
      return;
    }
    
    // Prevent concurrent refreshes
    if (this.isRefreshing || (window.cartDrawerInstance && window.cartDrawerInstance.isRefreshing)) {
      return;
    }
    
    // Use existing cart.js refresh logic if available
    if (window.cartDrawerInstance && typeof window.cartDrawerInstance.refreshCart === 'function') {
      // Don't await - let it run in background
      window.cartDrawerInstance.refreshCart().catch(err => {
        console.error('[CART] Error refreshing:', err);
      });
    } else {
      this.refreshCart();
    }
  }

  open() {
    // Re-query elements in case they weren't found during initialization
    if (!this.drawer || !this.panel) {
      this.initializeElements();
    }
    
    // Still not found? Log error but don't set body overflow (would freeze page)
    if (!this.drawer || !this.panel) {
      console.error('[CART] Drawer elements not found - cannot open');
      return;
    }

    // Store active element for focus return
    this.activeElement = document.activeElement;

    // Add active class to show drawer
    this.classList.add('active');
    this.drawer.classList.add('is-open');
    
    // Only set body overflow AFTER successfully adding active class
    document.body.style.overflow = 'hidden';

    // Force transform immediately for smooth animation
    this.panel.style.transition = 'none';
    this.panel.style.transform = 'translateX(0)';
    
    // Force reflow
    void this.panel.offsetHeight;

    // Restore transition
    requestAnimationFrame(() => {
      this.panel.style.transition = '';
    });

    // Focus management - use preventScroll to avoid jumping to top of page
    const focusable = this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) {
      setTimeout(() => {
        focusable.focus({ preventScroll: true });
      }, 100);
    }
  }

  close() {
    this.classList.remove('active');
    
    // Defensive check for drawer element
    if (this.drawer) {
      this.drawer.classList.remove('is-open');
    }
    
    // ALWAYS reset body overflow when closing
    document.body.style.overflow = '';

    // Return focus - use preventScroll to prevent page jumping
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      try {
        this.activeElement.focus({ preventScroll: true });
      } catch (e) {
        // Ignore focus errors
      }
    }
    this.activeElement = null;
  }

  handleOverlayClick(e) {
    if (e.target === this.overlay) {
      this.close();
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.classList.contains('active')) {
      this.close();
    }
  }

  async refreshCart() {
    // Prevent concurrent refreshes
    if (this.isRefreshing) {
      return;
    }
    
    this.isRefreshing = true;
    
    try {
      // Prefer using existing cart.js refresh logic
      if (window.cartDrawerInstance && typeof window.cartDrawerInstance.refreshCart === 'function') {
        await window.cartDrawerInstance.refreshCart();
        // Re-initialize item listeners only (not global event listeners)
        this.reinitItemListeners();
        return;
      }
      
      // Fallback: Try to fetch updated cart drawer section
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}?section_id=cart-drawer`);
      if (!response.ok) throw new Error('Failed to fetch cart');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('cart-drawer');

      if (newDrawer) {
        // Update items container
        const newItemsContainer = newDrawer.querySelector('[data-cart-drawer-items]');
        if (newItemsContainer && this.itemsContainer) {
          this.itemsContainer.innerHTML = newItemsContainer.innerHTML;
        }

        // Update footer
        const newFooter = newDrawer.querySelector('[data-cart-footer]');
        if (newFooter && this.footer) {
          this.footer.innerHTML = newFooter.innerHTML;
        }

        // Re-initialize item listeners only
        this.reinitItemListeners();
      }
    } catch (error) {
      console.error('[CART] Error refreshing cart:', error);
    } finally {
      this.isRefreshing = false;
    }
  }

  reinitItemListeners() {
    // Only re-initialize close buttons (not global event listeners)
    this.closeButtons = this.querySelectorAll('[data-cart-drawer-close]');
    this.closeButtons.forEach(btn => {
      // Remove old listeners and add new ones
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      newBtn.addEventListener('click', () => this.close());
    });
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.escapeKeyListenerAdded) {
      document.removeEventListener('keydown', this.onEscapeKey);
    }
    if (this.cartUpdateListenerAdded) {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.onOverlayClick);
    }
  }
}

// Define custom element
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawerElement);
}

// Export for global access
window.CartDrawerElement = CartDrawerElement;
